import React from 'react';
import classNames from 'classnames';

import Tooltip from '../Tooltip';

import {
  ISettingsLink,
  ISettingsLinks,
  PodcastServiceType,
} from '@wix/podcasts-common/dist/settings';

import { IWithSettingsProps, withSettings } from '../../context/settings';
import { withConfig, IWithConfigProps } from '../../context/config';
import { withI18n, IWithI18nProps } from '../../context/i18n';
import { withTPA, IWithTPAProps } from '../../context/tpa';
import { TPADeviceType } from '@wix/podcasts-common/dist/tpa';

import compose from '../../../lib/utils/compose';
import getFeedUrl from '../../../lib/settings/getFeedUrl';
import Wix from '../../../lib/tpa/Wix';
import getFontStyles from '../../../lib/styles/getFontStyles';

import SUBSCRIBE_ICONS from '../icons/subscribe';

import styles from './SubscribeLinks.scss';
import getStyles from './SubscribeLinks.styles';

import { Button } from '../buttons';
import Icon from './Icon';

const TOOLTIP_MESSAGES = {
  [PodcastServiceType.RSS]: 'subscribe.tooltip.rss',
  [PodcastServiceType.APPLE]: 'subscribe.tooltip.apple',
  [PodcastServiceType.SPOTIFY]: 'subscribe.tooltip.spotify',
  [PodcastServiceType.GOOGLE]: 'subscribe.tooltip.google',
  [PodcastServiceType.STITCHER]: 'subscribe.tooltip.stitcher',
  [PodcastServiceType.SOUND_CLOUD]: 'subscribe.tooltip.soundCloud',
};

function getFont(settings, placement) {
  const font = {
    [SubscribeLinksPlacement.MainHeader]: settings.fonts.main.headerTitleFont,
    [SubscribeLinksPlacement.MainMinimalHeader]:
      settings.fonts.main.headerTitleFont,
    [SubscribeLinksPlacement.EpisodeHeader]:
      settings.fonts.episode.headerTitleFont,
    [SubscribeLinksPlacement.Feed]: settings.fonts.main.episodeTitleFont,
  }[placement];

  return getFontStyles(font);
}

function getOrigin() {
  const { origin, protocol, hostname, port } = window.location;
  if (!origin) {
    return protocol + '//' + hostname + (port ? ':' + port : '');
  }
  return origin;
}

function normalizeUrl(url: string): string {
  return url.startsWith('http://') || url.startsWith('https://')
    ? url
    : `https://${url}`;
}

function prepareLinks(links: ISettingsLinks, rssUrl: string): ISettingsLinks {
  return links.map((link) => ({
    ...link,
    url: normalizeUrl(link.type === PodcastServiceType.RSS ? rssUrl : link.url),
  }));
}

export enum SubscribeLinksPlacement {
  MainHeader = 'MainHeader',
  MainMinimalHeader = 'MainMinimalHeader',
  EpisodeHeader = 'EpisodeHeader',
  Feed = 'Feed',
}

interface ISubscribeLinksProps
  extends IWithSettingsProps,
    IWithConfigProps,
    IWithI18nProps,
    IWithTPAProps {
  placement: SubscribeLinksPlacement;
}
interface ILinkProps extends ISettingsLink {
  tooltipText: string;
  tooltipStyles: any;
  className: string;
}

const Link: React.FC<ILinkProps> = ({
  type,
  url,
  className,
  tooltipText,
  tooltipStyles,
}) => {
  const LinkIcon = SUBSCRIBE_ICONS[type];

  return (
    <Tooltip content={tooltipText} styles={tooltipStyles}>
      <a className={styles.link} href={url} target="_blank">
        <span className={className}>
          <LinkIcon />
        </span>
      </a>
    </Tooltip>
  );
};

const DesktopSubscribeLinks: React.FC<ISubscribeLinksProps> = ({
  settings,
  config,
  placement,
  t,
}) => {
  const dynamicStyles = getStyles({
    mainColors: settings.colors.main,
    episodeColors: settings.colors.episode,
    buttonStyle: settings.common.buttonStyle,
  });
  const rssUrl = getFeedUrl(settings, config.defaultFeedUrl);
  const links = prepareLinks(settings.links, rssUrl);

  const wrapperClassName = classNames({
    [styles.feedLinksWrapper]: placement === SubscribeLinksPlacement.Feed,
    [styles.mainLinksWrapper]: placement === SubscribeLinksPlacement.MainHeader,
    [styles.episodeLinksWrapper]:
      placement === SubscribeLinksPlacement.EpisodeHeader,
  });

  const linkClassName = classNames(styles.icon, dynamicStyles.icon, {
    [dynamicStyles.mainHeader]:
      placement === SubscribeLinksPlacement.MainHeader ||
      placement === SubscribeLinksPlacement.MainMinimalHeader,
    [dynamicStyles.episodeHeader]:
      placement === SubscribeLinksPlacement.EpisodeHeader,
    [dynamicStyles.feed]: placement === SubscribeLinksPlacement.Feed,
  });

  return (
    <div className={wrapperClassName}>
      <div className={styles.links}>
        {links.map(({ type, url }) => (
          <Link
            className={linkClassName}
            type={type}
            url={url}
            key={type}
            tooltipText={t(TOOLTIP_MESSAGES[type])}
            tooltipStyles={{
              tooltip: { fontFamily: getFont(settings, placement).fontFamily },
            }}
          />
        ))}
      </div>
    </div>
  );
};

const MobileSubscribeLinks: React.FC<ISubscribeLinksProps> = ({
  settings,
  placement,
  config,
  t,
}) => {
  if (!settings.links || !settings.links.length) {
    return null;
  }

  const dynamicStyles = getStyles({
    mainColors: settings.colors.main,
    episodeColors: settings.colors.episode,
    buttonStyle: settings.common.buttonStyle,
  });

  const buttonClassName = classNames(
    styles.mobileButton,
    {
      [dynamicStyles.mobileButtonHeader]:
        placement === SubscribeLinksPlacement.MainHeader ||
        placement === SubscribeLinksPlacement.MainMinimalHeader,
    },
    {
      [dynamicStyles.mobileButtonEpisode]:
        placement === SubscribeLinksPlacement.EpisodeHeader,
    },
    {
      [dynamicStyles.mobileButtonFeed]:
        placement === SubscribeLinksPlacement.Feed,
    },
  );

  const rssUrl = getFeedUrl(settings, config.defaultFeedUrl);
  const links = prepareLinks(settings.links, rssUrl);

  const modalData = {
    title: t('subscribe.modalTitle'),
    links: links.map((link) => ({
      ...link,
      title: t(TOOLTIP_MESSAGES[link.type]),
    })),
    font: getFont(settings, placement),
  };
  const openModal = () =>
    Wix.openPopup(
      `${getOrigin()}/widget/subscribe?data=${encodeURIComponent(
        JSON.stringify(modalData),
      )}`,
      '100%',
      '100%',
      null,
      null,
    );

  return (
    <Button className={buttonClassName} onClick={openModal}>
      <Icon /> {t('subscribe.button')}
    </Button>
  );
};

const SubscribeLinks: React.FC<ISubscribeLinksProps> = (props) =>
  props.tpa.params.deviceType === TPADeviceType.DESKTOP ? (
    <DesktopSubscribeLinks {...props} />
  ) : (
    <MobileSubscribeLinks {...props} />
  );

export default compose(
  withConfig,
  withSettings,
  withI18n,
  withTPA,
)(SubscribeLinks);
